import React from "react";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useAzureStorageConnect } from "../hooks/useAzureStorageConnect";

interface AzureStorageFormData {
  storageAccountName: string;
  accessKey: string;
}

interface AzureStorageFormProps {
  onSuccess?: (storageAccountName: string) => void;
}

export function AzureStorageForm({ onSuccess }: AzureStorageFormProps) {
  const [formData, setFormData] = React.useState<AzureStorageFormData>({
    storageAccountName: "",
    accessKey: "",
  });

  const [errors, setErrors] = React.useState<Partial<AzureStorageFormData>>({});

  const {
    mutate: connectAzureStorage,
    isPending: isConnecting,
    error,
  } = useAzureStorageConnect();

  const validateForm = () => {
    const newErrors: Partial<AzureStorageFormData> = {};

    if (!formData.storageAccountName.trim()) {
      newErrors.storageAccountName = "Storage account name is required";
    }

    if (!formData.accessKey.trim()) {
      newErrors.accessKey = "Access key is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    field: keyof AzureStorageFormData,
    value: string,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      connectAzureStorage(
        {
          storage_account_name: formData.storageAccountName,
          access_token: formData.accessKey,
        },
        {
          onSuccess: () => {
            onSuccess?.(formData.storageAccountName);
          },
          onError: (error) => {
            console.error("Azure Storage connection failed:", error);
          },
        },
      );
    }
  };

  const isFormValid =
    formData.storageAccountName.trim() && formData.accessKey.trim();

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Storage Account Name Field */}
      <div className="space-y-1">
        <Label
          htmlFor="storageAccountName"
          className="text-base font-medium text-black dark:text-white"
        >
          Storage Account Name
        </Label>
        <Input
          id="storageAccountName"
          type="text"
          placeholder="Enter your account name"
          value={formData.storageAccountName}
          onChange={(e) =>
            handleInputChange("storageAccountName", e.target.value)
          }
          className="w-full"
          disabled={isConnecting}
        />
        {errors.storageAccountName && (
          <p className="text-sm text-red-500">{errors.storageAccountName}</p>
        )}
      </div>

      {/* Access Key Field */}
      <div className="space-y-1">
        <Label
          htmlFor="accessKey"
          className="text-base font-medium text-black dark:text-white"
        >
          Access Key
        </Label>
        <Input
          id="accessKey"
          type="password"
          placeholder="Enter access key to your bucket"
          value={formData.accessKey}
          onChange={(e) => handleInputChange("accessKey", e.target.value)}
          className="w-full"
          disabled={isConnecting}
        />
        {errors.accessKey && (
          <p className="text-sm text-red-500">{errors.accessKey}</p>
        )}
      </div>

      {error && (
        <div className="text-sm text-red-500">
          Failed to connect: {error.message}
        </div>
      )}

      <Button
        type="submit"
        className="w-full h-10"
        disabled={!isFormValid || isConnecting}
      >
        {isConnecting ? "Connecting..." : "Connect to Azure Storage"}
      </Button>
    </form>
  );
}

export type { AzureStorageFormData };
